import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { faqErrorCode, faqErrorMessage, importFaqLegacy, listFaqLegacyDocuments } from '../../api/faq'
import { TONES, fieldLabel, reportMessage, toneOf } from './faqReport'
import useDialogKeys from './useDialogKeys'

const button = 'h-10 rounded-lg px-4 text-sm font-semibold disabled:opacity-50'

function ReportItem({ item, onAddManually }) {
  const { t } = useTranslation()
  return (
    <li className={`flex flex-wrap items-start justify-between gap-2 rounded-lg border p-2 text-sm ${TONES[toneOf(item.code)]}`}>
      <p className="min-w-0 text-slate-800">
        <span className="font-semibold">{t('faq.legacy.pair', { index: item.index })}</span>
        {item.field && <span className="text-slate-500"> · {fieldLabel(t, item.field)}</span>}
        <span>: {reportMessage(t, item)}</span>
      </p>
      {/* Too long: the manager can shorten it by hand. Other refusals (the 500 cap...) cannot be fixed there. */}
      {item.prefill && item.code === 'too_long' && (
        <button
          type="button" onClick={() => onAddManually(item.prefill)}
          className="shrink-0 text-sm font-semibold text-[#1535c8] underline underline-offset-2"
        >
          {t('faq.legacy.addManually')}
        </button>
      )}
    </li>
  )
}

// Turns the Q:/A: pairs of one old `faq` knowledge document into FAQ entries:
// a dry run first, then the import. The document itself is never changed.
export default function FaqLegacyImportDialog({ tenantId, onImported, onAddManually, onClose, onPermissionDenied }) {
  const { t, i18n } = useTranslation()
  const [documents, setDocuments] = useState(null) // null while loading
  const [loadError, setLoadError] = useState(null)
  const [documentId, setDocumentId] = useState('')
  const [step, setStep] = useState('choose') // choose | preview | done
  const [report, setReport] = useState(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const root = useRef(null)
  const onKeyDown = useDialogKeys(root, () => !busy && onClose())
  // Focus returns to the dialog when the focused control is disabled or gone with the step.
  useEffect(() => {
    if (!busy && !root.current?.contains(document.activeElement)) root.current?.focus()
  }, [busy, step])

  useEffect(() => {
    let alive = true
    listFaqLegacyDocuments(tenantId)
      .then((items) => alive && setDocuments(items))
      .catch((error) => alive && setLoadError(error))
    return () => {
      alive = false
    }
  }, [tenantId])

  const number = (value) => Number(value).toLocaleString(i18n.language)
  const counted = (value) => ({ count: value, formatted: number(value) })

  const run = async (dryRun) => {
    setBusy(true)
    setMessage('')
    try {
      const result = await importFaqLegacy(tenantId, documentId, { dryRun })
      setReport(result)
      setStep(dryRun ? 'preview' : 'done')
      if (!dryRun) onImported(result)
    } catch (error) {
      setMessage(faqErrorMessage(t, error))
      if (faqErrorCode(error) === 'permission_denied') onPermissionDenied?.()
    } finally {
      setBusy(false)
    }
  }

  // Leaving the dialog before the import drops its check: say so when there was something to import.
  const addManually = (prefill) => {
    if (step === 'preview' && report.created > 0 && !window.confirm(t('faq.legacy.leaveConfirm', counted(report.created)))) return
    onAddManually(prefill)
  }

  const kilobytes = (size) => number(Math.max(1, Math.round(size / 1024)))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        ref={root} role="dialog" aria-modal="true" aria-label={t('faq.legacy.title')} tabIndex={-1}
        onKeyDown={onKeyDown} aria-busy={busy}
        className="flex max-h-full w-full max-w-2xl flex-col gap-3 overflow-y-auto rounded-lg bg-white p-4 outline-none"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{t('faq.legacy.title')}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t('faq.panel.close')}>
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {step === 'choose' && (
          <>
            <p className="text-sm text-slate-600">{t('faq.legacy.intro')}</p>
            {loadError ? (
              <p role="alert" className="text-sm text-red-600">{faqErrorMessage(t, loadError)}</p>
            ) : documents === null ? (
              <p className="text-sm text-slate-500">{t('common.loading')}</p>
            ) : documents.length === 0 ? (
              <p className="text-sm text-slate-500">{t('faq.legacy.none')}</p>
            ) : (
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-sm font-medium text-slate-700">{t('faq.legacy.chooseDocument')}</legend>
                {documents.map((doc) => (
                  <label key={doc.id} className="flex items-start gap-2 rounded-lg border border-slate-200 p-2 text-sm">
                    <input
                      type="radio" name="faq-legacy-document" value={doc.id}
                      checked={documentId === doc.id} onChange={() => setDocumentId(doc.id)}
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <span className="block break-words font-medium text-slate-800">{doc.title}</span>
                      <span className="block text-xs text-slate-500">
                        {t('faq.legacy.size', { size: kilobytes(doc.size) })}
                        {doc.imported_count > 0 && ` · ${t('faq.legacy.alreadyImported', counted(doc.imported_count))}`}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
          </>
        )}

        {step !== 'choose' && report && (
          <>
            {step === 'done' ? (
              <p className="text-sm text-slate-700">{t('faq.legacy.done', counted(report.created))}</p>
            ) : report.pairs_total === 0 ? (
              <p className="text-sm text-slate-700">{t('faq.legacy.noPairs')}</p>
            ) : (
              <ul className="flex flex-wrap gap-2 text-sm">
                <li className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{t('faq.legacy.summary.pairs', counted(report.pairs_total))}</li>
                {['created', 'duplicate', 'invalid'].map((name) => (
                  <li key={name} className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                    {t(`faq.legacy.summary.${name}`, counted(report[name]))}
                  </li>
                ))}
              </ul>
            )}
            {report.report.length > 0 && (
              <ul className="flex flex-col gap-2">
                {report.report.map((item, index) => <ReportItem key={index} item={item} onAddManually={addManually} />)}
              </ul>
            )}
            {report.truncated && <p className="text-xs text-slate-500">{t('faq.import.truncated')}</p>}
            {step === 'preview' && <p className="text-xs text-slate-500">{t('faq.legacy.untouched')}</p>}
          </>
        )}

        {message && <p role="alert" className="text-sm text-red-600">{message}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          {step === 'choose' && (
            <button type="button" onClick={() => run(true)} disabled={!documentId || busy} className={`${button} bg-[#1535c8] text-white`}>
              {busy ? t('faq.import.checking') : t('faq.legacy.check')}
            </button>
          )}
          {step === 'preview' && (
            <>
              <button
                type="button" onClick={() => { setMessage(''); setStep('choose') }} disabled={busy}
                className={`${button} border border-slate-200 bg-white text-slate-700`}
              >
                {t('faq.legacy.chooseAnother')}
              </button>
              <button
                type="button" onClick={() => run(false)} disabled={busy || report.created === 0}
                className={`${button} bg-[#1535c8] text-white`}
              >
                {busy ? t('faq.import.committing') : t('faq.import.commit', counted(report.created))}
              </button>
            </>
          )}
          {step === 'done' && (
            <button type="button" onClick={onClose} className={`${button} bg-[#1535c8] text-white`}>
              {t('faq.import.finish')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
